import * as appTypes from '@/store/actions/app'
import * as authTypes from '@/store/actions/auth'
import * as api from '@/utils/api'
import { router, resetRouter } from '@/router'
import menu from '@/utils/menu'

// initial state
const state = {
  currentContent: {
    id: null,
    name: null
  },
  currentMenu: [],
  currentLanguage: null, // appTypes.APP_DEFAULT_LANGUAGE,
  detailShown: false,
  detailContent: null,
  packageVersion: process.env.PACKAGE_VERSION || '0',
  appLoading: true,
  visuals: [],
  detailTitle: '',
  generalSidebar: null
}

// getters
const getters = {
  [appTypes.APP_CURRENT_CONTENT]: state => state.currentContent,
  [appTypes.APP_CURRENT_CONTENT_ID]: state => state.currentContent.id,
  [appTypes.APP_CURRENT_CONTENT_CLASS]: state => (state.currentContent.id === 'login' ? 'login' : state.currentContent.id + ' logged'),
  [appTypes.APP_CURRENT_CONTENT_NAME]: state => state.currentContent.name,
  [appTypes.APP_CURRENT_MENU]: state => state.currentMenu,
  [appTypes.APP_CURRENT_LANGUAGE]: state => state.currentLanguage,
  [appTypes.APP_DETAIL_SHOWN]: state => state.detailShown,
  [appTypes.APP_DETAIL_CONTENT]: state => state.detailContent,
  [appTypes.APP_PACKAGE_VERSION]: state => state.packageVersion,
  [appTypes.APP_LOADING]: state => state.appLoading,
  [appTypes.APP_VISUALS]: state => state.visuals,
  [appTypes.APP_DETAIL_TITLE]: state => state.detailTitle,
  [appTypes.APP_GENERAL_SIDEBAR]: state => state.generalSidebar
}

// actions
const actions = {
  [appTypes.APP_CURRENT_CONTENT] ({ commit, state }, currentContent) {
    commit(appTypes.APP_CURRENT_CONTENT, { currentContent })
  },
  [appTypes.APP_CURRENT_MENU] ({ commit, state }, currentMenu) {
    var resp = null
    var path = null
    var currentContent = null
    var content = null

    if (currentMenu) {
      resp = currentMenu.resp
      if (currentMenu.to) {
        path = currentMenu.to.fullPath
      }
    }

    if (!path) {
      currentContent = menu.methods.getDefaultCurrentContent(resp)
    } else {
      content = menu.methods.getContent(resp, path)
      if (content.currentContent) {
        currentContent = content.currentContent
      } else {
        currentContent = menu.methods.getDefaultCurrentContent(resp)
      }
    }

    commit(appTypes.APP_CURRENT_MENU, { resp, currentContent })
    var menuRoutes = menu.methods.getMenuAppRoutes(resp)

    resetRouter()
    if (menuRoutes) {
      for (var i = 0; i < menuRoutes.length; i++) {
        router.addRoute(menuRoutes[i])
      }
    }

    if (content && content.detailContent && content.detailContentId) {
      menu.methods.loadDetailView(content.detailContent.id, content.detailContentId)
    } else if (currentContent) {
      router.push(currentContent.parentContextPath + currentContent.contextPath).catch(() => {})
    }
  },
  [appTypes.APP_CURRENT_LANGUAGE] ({ commit, state }, language) {
    window.localStorage.setItem('lang', language.lang)
    language.ml.change(language.lang)
    commit(appTypes.APP_CURRENT_LANGUAGE, { language: language.lang })
  },
  [appTypes.APP_MENU]: ({ commit, dispatch }, to) => {
    commit(appTypes.APP_LOADING, { loading: true })
    api.call({access: api.ACCESS_FRAME, url: '/menu', method: 'POST'})
      .then(resp => {
        dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_CURRENT_MENU}`, {resp, to}, {root: true})
        dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_VISUALS}`, resp, {root: true})
      })
      .catch(err => {
        // open error service dialog
        console.error(err)
        commit(appTypes.APP_LOADING, { loading: false })
        dispatch(`${authTypes.AUTH_NAMESPACE}/${authTypes.AUTH_STATUS_ERROR}`, {err}, {root: true})
      })
  },
  [appTypes.APP_DETAIL_SHOWN] ({ commit, state }, detailShown) {
    commit(appTypes.APP_DETAIL_SHOWN, { detailShown })
  },
  [appTypes.APP_DETAIL_CONTENT] ({ commit, state }, detailContent) {
    commit(appTypes.APP_DETAIL_CONTENT, { detailContent })
  },
  [appTypes.APP_LOADING] ({ commit, state }, loading) {
    commit(appTypes.APP_LOADING, { loading })
  },
  [appTypes.APP_VISUALS] ({ commit, dispatch }, menu) {
    if (menu) {
      for (var i = 0; i < menu.length; i++) {
        commit(appTypes.APP_VISUALS, menu[i])

        if (menu[i].subMenu) {
          dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_VISUALS}`, menu[i].subMenu, {root: true})
        }
      }
    }
  },
  [appTypes.APP_DETAIL_TITLE] ({ commit, state }, detailTitle) {
    commit(appTypes.APP_DETAIL_TITLE, { detailTitle })
  },
  [appTypes.APP_GENERAL_SIDEBAR] ({ commit, state }, generalSidebar) {
    commit(appTypes.APP_GENERAL_SIDEBAR, { generalSidebar })
  }
}

// mutations
const mutations = {
  [appTypes.APP_CURRENT_CONTENT] (state, { currentContent }) {
    if (currentContent) {
      state.currentContent = currentContent
    }
  },
  [appTypes.APP_CURRENT_MENU] (state, { resp, currentContent }) {
    if (resp) {
      state.currentMenu = resp
    }
    if (currentContent) {
      state.currentContent = currentContent
    }
  },
  [appTypes.APP_CURRENT_LANGUAGE] (state, { language }) {
    if (language) {
      state.currentLanguage = language
    }
  },
  [appTypes.APP_DETAIL_SHOWN] (state, { detailShown }) {
    state.detailShown = detailShown
    if (!state.detailShown) {
      state.detailContent = null
    }
  },
  [appTypes.APP_DETAIL_CONTENT] (state, { detailContent }) {
    if (detailContent) {
      state.detailContent = detailContent.resp
      state.detailContent.endpoint = detailContent.endpoint
      state.detailContent.id = detailContent.id
      state.detailContent.name = detailContent.name
    }
  },
  [appTypes.APP_LOADING] (state, { loading }) {
    state.appLoading = loading
  },
  [appTypes.APP_VISUALS] (state, { visual }) {
    if (visual) {
      try {
        state.visuals.push(JSON.parse(visual.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ')))
      } catch (err) {
        console.error('Visuals error: ' + err + ' --> ' + visual)
      }
    }
  },
  [appTypes.APP_DETAIL_TITLE] (state, { detailTitle }) {
    state.detailTitle = detailTitle
  },
  [appTypes.APP_GENERAL_SIDEBAR] (state, { generalSidebar }) {
    state.generalSidebar = generalSidebar
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/app.js